// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import NoSSR from 'vue-no-ssr';
import VueEasyLightbox from 'vue-easy-lightbox';
import { BootstrapBadge, BootstrapButton, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../../dsl/front';
import { AppTheme, ImageRatio } from '../../_contracts';
import { prepareImage } from '../../_filters';
import { AdBannerModuleUi } from '../../AdBanner/ui';
import { ArticleActions } from '../ArticleActions';
import { ArticleShare } from '../ArticleShare';
import { ArticleSocials } from '../ArticleSocials';
import { Gallery } from '../Gallery';
import { ImageWithCaption } from '../ImageWithCaption';
import { LineHeader } from '../LineHeader/ui';
import { PlayIcon } from '../PlayIcon';
import { ReadingTime } from '../ReadingTime';
import { ArticleHeaderImage } from './partials/ArticleHeaderImage';
import { ArticleHeaderVideo } from './partials/ArticleHeaderVideo';
import { ArticleAnchors } from './partials/ArticleAnchors';
import { ArticleAuthorBlock } from './partials/ArticleAuthorBlock';
/**
 * @author Sebastian Dziechciarz <sebastian.dziechciarz@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
let ArticleHeader = class ArticleHeader extends Vue {
    constructor() {
        super(...arguments);
        this.AppTheme = AppTheme;
        this.maxWidth = this.isMobile ? 768 : 800;
    }
    get hasGallery() {
        return typeof this.content.cover === 'object' && Array.isArray(this.content.cover.gallery) && this.content.cover.gallery !== null;
    }
    get hasVideo() {
        return !!this.content.cover.video && ((typeof this.content.cover.video.url === 'string' && this.content.cover.video.url.length > 0) ||
            (typeof this.content.cover.video.onNetworkUrl === 'string' && this.content.cover.video.onNetworkUrl.length > 0) ||
            (!!this.content.cover.video.file && typeof this.content.cover.video.file === 'object' &&
                (!!this.content.cover.video.file.url || !!this.content.cover.video.file.m3u8)));
    }
    get thumbnail() {
        if (!this.content.cover.image) {
            return null;
        }
        return prepareImage(this.content.cover.image, ImageRatio.Standard169, this.maxWidth, this.isLazy);
    }
    get hasThumbnail() {
        return this.thumbnail !== null;
    }
    get overridenCaption() {
        return (typeof this.content.cover === 'object' && this.content.cover.override === true) ? this.content.cover.caption : undefined;
    }
    get caption() {
        var _a, _b;
        return (_b = (this.overridenCaption ? this.overridenCaption : (_a = this.thumbnail) === null || _a === void 0 ? void 0 : _a.caption)) !== null && _b !== void 0 ? _b : '';
    }
    get images() {
        if (!this.hasGallery) {
            return [];
        }
        if (!this.content.cover.gallery) {
            return [];
        }
        return this.content.cover.gallery;
    }
    get shouldHideDescription() {
        if (!this.content.cover.hideDescription) {
            return false;
        }
        return this.content.cover.hideDescription;
    }
    get videoProps() {
        var _a;
        return {
            ...this.videoConfig,
            name: (_a = (this.caption || this.content.meta.title)) !== null && _a !== void 0 ? _a : '',
            settings: this.content.cover.videoSettings,
            thumbnail: this.thumbnail,
            uploadDate: this.content.publicationDate,
            video: this.content.cover.video
        };
    }
    get sponsored() {
        return this.content.options.sponsored;
    }
    onSocialClick(social) {
        this.$emit('social-click', social);
    }
};
__decorate([
    Prop({ type: Object, required: true })
], ArticleHeader.prototype, "content", void 0);
__decorate([
    Prop({ type: String, required: false, default: '' })
], ArticleHeader.prototype, "fallbackImage", void 0);
__decorate([
    Prop({ type: Boolean, required: false, default: true })
], ArticleHeader.prototype, "isMobile", void 0);
__decorate([
    Prop({ type: Boolean, required: false, default: false })
], ArticleHeader.prototype, "isLazy", void 0);
__decorate([
    Prop({ type: Object, required: true })
], ArticleHeader.prototype, "videoConfig", void 0);
__decorate([
    Prop({ type: Object, required: false, default: null })
], ArticleHeader.prototype, "pdfAction", void 0);
__decorate([
    Prop({ type: Array, required: true })
], ArticleHeader.prototype, "actions", void 0);
__decorate([
    Prop({ type: Object, required: true })
], ArticleHeader.prototype, "windowService", void 0);
__decorate([
    Inject({ from: 'theme', default: AppTheme.Default })
], ArticleHeader.prototype, "theme", void 0);
ArticleHeader = __decorate([
    Component({
        name: 'ArticleHeader',
        components: {
            AdBannerModuleUi,
            ArticleActions,
            ArticleAnchors,
            ArticleAuthorBlock,
            ArticleHeaderVideo,
            ArticleHeaderImage,
            ArticleShare,
            ArticleSocials,
            BootstrapBadge,
            BootstrapButton,
            BootstrapIcon,
            BootstrapImage,
            BootstrapLink,
            Gallery,
            ImageWithCaption,
            LineHeader,
            NoSSR,
            PlayIcon,
            ReadingTime,
            VueEasyLightbox
        },
        template: `
    <header class="article-header" :class="{ 'article-header--video': hasVideo }">
      <ul class="article-header__labels list-inline">
        <li v-if="sponsored" class="article-header__label">
          <BootstrapBadge>sponsorowane</BootstrapBadge>
        </li>
        <li v-for="(label, index) in content.labels" :key="label.name + index">
          <BootstrapBadge :theme="label.color">
            {{ label.name }}
          </BootstrapBadge>
        </li>
      </ul>

      <h1 class="article-header__title">{{ content.title }}</h1>

      <ArticleAuthorBlock
        v-if="content.author"
        :author="content.author"
        :publicationDate="content.publicationDate"
        :readingTime="content.readingTime"
        :hideDate="content.options && content.options.hidePublicationDate"
        :pdfAction="pdfAction"
        :isMobile="isMobile"
      />

      <div v-if="content.lead" v-html="content.lead" class="article-header__lead" />

      <!--Cover image / Gallery -->
      <Gallery
        v-if="hasGallery"
        class="article-header__gallery"
        :images="images"
        :maxWidth="maxWidth"
        :windowService="windowService"
        fullScreen
      />
      <!-- END | Cover image / Gallery -->

      <!--Cover image / Single image -->
      <ArticleHeaderImage
        v-else-if="hasThumbnail || fallbackImage"
        :caption="hasVideo ? undefined : overridenCaption"
        :hideDescription="hasVideo || shouldHideDescription"
        :hideAuthor="hasVideo"
        :isLazy="isLazy"
        :thumbnail="thumbnail"
        :fallbackImage="fallbackImage"
      >
        <template v-slot:video v-if="hasVideo">
          <ArticleHeaderVideo
            :config="videoProps"
            @pause="payload => $emit('pause', payload)"
            @play="payload => $emit('play', payload)"
          />
        </template>

        <template v-slot:ad>
          <slot name="ad" />
        </template>
      </ArticleHeaderImage>
      <!-- END | Cover image -->

      <!-- Print, download & social buttons -->
      <div class="article-header__actions__wrap">
        <ArticleActions class="article-header__actions" v-bind="{ actions }" />
      </div>

      <ArticleAnchors v-if="content.anchors" :anchors="content.anchors" />

      <!-- ld+json -->
      <slot />
    </header>
  `
    })
], ArticleHeader);
export { ArticleHeader };
export default ArticleHeader;
